import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import RedeemIcon from "@mui/icons-material/Redeem";
import RuleIcon from "@mui/icons-material/Rule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import SchoolIcon from "@mui/icons-material/School";
import { toast } from "react-toastify";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  getReferralSettings,
  resetReferralSettings,
  updateReferralSettings,
} from "services/referralService";
import ArgonInput from "components/ArgonInput";

const defaultParentTerms = [
  "Referred parent must be unique and not already verified with Edushala.",
  "Parent referral bonus will be credited after first payment/subscription.",
  "Ensure the referred parent completes the required onboarding process.",
  "Minimum redeem amount is Rs 1000.",
  "Referral bonus is non-transferable and cannot be exchanged for cash directly.",
];

const defaultTutorTerms = [
  "Referred tutor must be unique and not already verified with Edushala.",
  "Tutor referral bonus will be credited after marking first attendance.",
  "Ensure the referred tutor completes the full onboarding process for validation.",
  "Minimum redeem amount is Rs 1000.",
  "Referral bonus is non-transferable and cannot be exchanged for cash directly.",
];

const createDefaultForm = (terms = [""]) => ({
  bonusAmount: "200",
  minimumRedeemAmount: "1000",
  termsAndConditions: terms,
  isActive: true,
});

const initialForm = {
  parentSettings: createDefaultForm(defaultParentTerms),
  tutorSettings: createDefaultForm(defaultTutorTerms),
};

const tabs = [
  {
    key: "parentSettings",
    label: "Parent",
    icon: <FamilyRestroomIcon fontSize="small" />,
    heading: "Parent Referral Settings",
  },
  {
    key: "tutorSettings",
    label: "Tutor",
    icon: <SchoolIcon fontSize="small" />,
    heading: "Tutor Referral Settings",
  },
];

const normalizeSettings = (settings, fallbackTerms) => ({
  bonusAmount: (settings?.bonusAmount ?? 200).toString(),
  minimumRedeemAmount: (settings?.minimumRedeemAmount ?? 1000).toString(),
  termsAndConditions:
    settings?.termsAndConditions?.length > 0 ? settings.termsAndConditions : fallbackTerms,
  isActive: settings?.isActive ?? true,
});

function ReferralSettings() {
  const [form, setForm] = useState(initialForm);
  const [activeTab, setActiveTab] = useState("parentSettings");
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");

  const activeForm = form[activeTab];
  const activeTabConfig = tabs.find((tab) => tab.key === activeTab);

  const applySettingsToForm = (settings) => {
    setForm({
      parentSettings: normalizeSettings(
        settings?.parentSettings || {
          bonusAmount: settings?.bonusAmount,
          minimumRedeemAmount: settings?.minimumRedeemAmount,
          termsAndConditions: defaultParentTerms,
          isActive: settings?.isActive,
        },
        defaultParentTerms
      ),
      tutorSettings: normalizeSettings(
        settings?.tutorSettings || {
          bonusAmount: settings?.bonusAmount,
          minimumRedeemAmount: settings?.minimumRedeemAmount,
          termsAndConditions: defaultTutorTerms,
          isActive: settings?.isActive,
        },
        defaultTutorTerms
      ),
    });
    setLastUpdated(settings?.updatedAt || "");
  };

  const fetchSettings = async () => {
    setFetchLoading(true);
    try {
      const response = await getReferralSettings();
      if (response.success) {
        applySettingsToForm(response.data);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch referral settings");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: value,
      },
    }));
  };

  const handleTermChange = (index, value) => {
    setForm((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        termsAndConditions: prev[activeTab].termsAndConditions.map((term, termIndex) =>
          termIndex === index ? value : term
        ),
      },
    }));
  };

  const handleAddTerm = () => {
    setForm((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        termsAndConditions: [...prev[activeTab].termsAndConditions, ""],
      },
    }));
  };

  const handleRemoveTerm = (index) => {
    setForm((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        termsAndConditions:
          prev[activeTab].termsAndConditions.length === 1
            ? [""]
            : prev[activeTab].termsAndConditions.filter((_, termIndex) => termIndex !== index),
      },
    }));
  };

  const validateSettings = (settings, label) => {
    const bonusAmount = Number(settings.bonusAmount);
    const minimumRedeemAmount = Number(settings.minimumRedeemAmount);

    if (Number.isNaN(bonusAmount) || bonusAmount < 0) {
      toast.error(`Please enter a valid ${label.toLowerCase()} referral bonus amount.`);
      return false;
    }

    if (Number.isNaN(minimumRedeemAmount) || minimumRedeemAmount < 0) {
      toast.error(`Please enter a valid ${label.toLowerCase()} minimum redeem amount.`);
      return false;
    }

    const terms = settings.termsAndConditions.map((term) => term.trim()).filter(Boolean);
    if (!terms.length) {
      toast.error(`Please add at least one ${label.toLowerCase()} term and condition.`);
      return false;
    }

    return true;
  };

  const validateForm = () =>
    tabs.every((tab) => validateSettings(form[tab.key], tab.label));

  const buildPayloadSettings = (settings) => ({
    bonusAmount: Number(settings.bonusAmount),
    minimumRedeemAmount: Number(settings.minimumRedeemAmount),
    termsAndConditions: settings.termsAndConditions.map((term) => term.trim()).filter(Boolean),
    isActive: settings.isActive,
  });

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      parentSettings: buildPayloadSettings(form.parentSettings),
      tutorSettings: buildPayloadSettings(form.tutorSettings),
      bonusAmount: Number(form.parentSettings.bonusAmount),
      minimumRedeemAmount: Number(form.parentSettings.minimumRedeemAmount),
      termsAndConditions: form.parentSettings.termsAndConditions
        .map((term) => term.trim())
        .filter(Boolean),
      isActive: form.parentSettings.isActive,
    };

    setSaving(true);
    try {
      const response = await updateReferralSettings(payload);
      if (response.success) {
        applySettingsToForm(response.data);
        toast.success("Referral settings updated successfully!");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update referral settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await resetReferralSettings();
      if (response.success) {
        applySettingsToForm(response.data);
        toast.success("Referral settings reset to default!");
      }
    } catch (error) {
      toast.error(error.message || "Failed to reset referral settings");
    } finally {
      setResetting(false);
    }
  };

  const formattedUpdatedAt = lastUpdated ? new Date(lastUpdated).toLocaleString() : "Not saved yet";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox
              display="flex"
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              mb={3}
              pb={2}
              borderBottom="1px solid #e9ecef"
            >
              <ArgonBox display="flex" alignItems="center" gap={1.5}>
                <SettingsSuggestIcon color="primary" sx={{ fontSize: 32 }} />
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    Referral Settings
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    Last updated: {formattedUpdatedAt}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              {!fetchLoading && (
                <ArgonBox display="flex" alignItems="center" gap={1.5}>
                  {activeForm.isActive ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <PauseCircleIcon color="warning" fontSize="small" />
                  )}
                  <ArgonTypography variant="button" fontWeight="medium">
                    {activeTabConfig.label} {activeForm.isActive ? "Active" : "Inactive"}
                  </ArgonTypography>
                  <Switch
                    checked={activeForm.isActive}
                    onChange={(event) => handleChange("isActive", event.target.checked)}
                    disabled={saving || resetting}
                  />
                </ArgonBox>
              )}
            </ArgonBox>

            {fetchLoading ? (
              <ArgonBox textAlign="center" py={5}>
                <CircularProgress color="primary" />
                <ArgonTypography variant="body2" color="text" mt={2}>
                  Loading referral settings...
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <>
                <ArgonBox mb={3} borderBottom="1px solid #e9ecef">
                  <Tabs
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {tabs.map((tab) => (
                      <Tab
                        key={tab.key}
                        value={tab.key}
                        icon={tab.icon}
                        iconPosition="start"
                        label={tab.label}
                        disabled={saving || resetting}
                      />
                    ))}
                  </Tabs>
                </ArgonBox>

                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  {activeTabConfig.heading}
                </ArgonTypography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                        <CurrencyRupeeIcon color="primary" fontSize="small" />
                        <ArgonTypography variant="caption" fontWeight="bold">
                          Referral Bonus Amount
                        </ArgonTypography>
                      </ArgonBox>
                      <TextField
                        fullWidth
                        type="number"
                        value={activeForm.bonusAmount}
                        onChange={(event) => handleChange("bonusAmount", event.target.value)}
                        disabled={saving || resetting}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CurrencyRupeeIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                        <RedeemIcon color="primary" fontSize="small" />
                        <ArgonTypography variant="caption" fontWeight="bold">
                          Minimum Redeem Amount
                        </ArgonTypography>
                      </ArgonBox>
                      <TextField
                        fullWidth
                        type="number"
                        value={activeForm.minimumRedeemAmount}
                        onChange={(event) => handleChange("minimumRedeemAmount", event.target.value)}
                        disabled={saving || resetting}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CurrencyRupeeIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ArgonBox>
                  </Grid>
                </Grid>

                <ArgonBox mt={2}>
                  <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <RuleIcon color="primary" fontSize="small" />
                      <ArgonTypography variant="h6" fontWeight="medium">
                        Terms and Conditions
                      </ArgonTypography>
                    </ArgonBox>
                    <Tooltip title="Add term">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={handleAddTerm}
                          disabled={saving || resetting}
                        >
                          <AddIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ArgonBox>

                  <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                    {activeForm.termsAndConditions.map((term, index) => (
                      <ArgonBox
                        key={`${activeTab}-term-${index}`}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <ArgonInput
                          fullWidth
                          multiline
                          value={term}
                          onChange={(event) => handleTermChange(index, event.target.value)}
                          disabled={saving || resetting}
                          placeholder={`Term ${index + 1}`}
                        />
                        <Tooltip title="Remove term">
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveTerm(index)}
                              disabled={saving || resetting}
                              sx={{ mt: 0.5 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ArgonBox>
                    ))}
                  </ArgonBox>
                </ArgonBox>

                <ArgonBox
                  display="flex"
                  justifyContent="flex-end"
                  flexWrap="wrap"
                  gap={1.5}
                  mt={4}
                  pt={3}
                  borderTop="1px solid #e9ecef"
                >
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleReset}
                    disabled={saving || resetting}
                  >
                    {resetting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <RestartAltIcon fontSize="small" />
                        Reset Default
                      </ArgonBox>
                    )}
                  </ArgonButton>
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || resetting}
                  >
                    {saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <SaveIcon fontSize="small" />
                        Save Settings
                      </ArgonBox>
                    )}
                  </ArgonButton>
                </ArgonBox>
              </>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ReferralSettings;